import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';

interface HSNResult {
  code: string;
  description: string;
  code_type: string;
  gst_rate: number | null;
  chapter: string | null;
  category: string | null;
}

interface HSNLookupProps {
  value: string;
  onChange: (code: string, meta?: HSNResult) => void;
  placeholder?: string;
  hasError?: boolean;
  id?: string;
}

/**
 * Typeahead lookup for HSN/SAC codes backed by the self-hosted /api/hsn/search
 * endpoint. Users can type a code or a product description; matching codes
 * appear in a dropdown with their GST rate. Free typing is allowed (the field
 * still accepts a manually entered code).
 */
const HSNLookup: React.FC<HSNLookupProps> = ({ value, onChange, placeholder, hasError, id }) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState<HSNResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const runSearch = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const res = await api.get('/api/hsn/search', { params: { q, limit: 15 } });
      setResults(res.data?.results || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (v: string) => {
    setQuery(v);
    onChange(v); // allow free typing; parent keeps the raw value
    setOpen(true);
    setActiveIdx(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v.trim()), 250);
  };

  const select = (r: HSNResult) => {
    setQuery(r.code);
    onChange(r.code, r);
    setOpen(false);
    setActiveIdx(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); select(results[activeIdx]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        id={id}
        type="text"
        value={query}
        placeholder={placeholder || 'Type a code or product description…'}
        autoComplete="off"
        onChange={(e) => handleInput(e.target.value)}
        onFocus={() => { setOpen(true); if (results.length === 0) runSearch(query.trim()); }}
        onKeyDown={handleKeyDown}
        className="form-input"
        style={{
          width: '100%', padding: '8px 12px', borderRadius: '8px', fontSize: '13px',
          border: `1px solid ${hasError ? '#ef4444' : '#e2e8f0'}`, background: 'white',
          fontFamily: /^\d/.test(query) ? "'SF Mono', 'Fira Code', monospace" : undefined,
        }}
      />
      {open && (loading || results.length > 0) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, marginTop: '2px',
          background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '260px', overflow: 'auto',
        }}>
          {loading && <div style={{ padding: '8px 12px', fontSize: '12px', color: '#94a3b8' }}>Searching…</div>}
          {!loading && results.map((r, idx) => (
            <div
              key={r.code}
              onMouseDown={(e) => { e.preventDefault(); select(r); }}
              onMouseEnter={() => setActiveIdx(idx)}
              style={{
                padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                background: idx === activeIdx ? '#f1f5f9' : 'white',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontFamily: "'SF Mono', 'Fira Code', monospace", fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                  {r.code}
                  <span style={{
                    marginLeft: '6px', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase',
                    color: r.code_type === 'sac' ? '#6366f1' : '#0d9488',
                    background: r.code_type === 'sac' ? '#eef2ff' : '#f0fdfa',
                    padding: '1px 6px', borderRadius: '6px',
                  }}>{r.code_type}</span>
                </span>
                {r.gst_rate != null && (
                  <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>
                    GST {r.gst_rate}%
                  </span>
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.description}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HSNLookup;
